/**
 * Tree-Navigation für den Voll-Page-Browser: Knoten per Pfad finden, Breadcrumb ableiten,
 * Ordner für den Move-Ziel-Picker flach sammeln, Datei-Suche im aktuellen Ordner.
 * Reine Funktionen — unit-testbar, keine React-Abhängigkeit.
 */
import type { PropertyFileResponse, PropertyFileTreeNode } from '@/shared/api';
import { segmentsOf } from './akteFolderRules';

/** Root-Node (`path === ''`) oder einen beliebig tiefen Node per vollem Pfad finden. */
export function findNodeByPath(
  root: PropertyFileTreeNode,
  path: string,
): PropertyFileTreeNode | null {
  if (path === root.path) return root;
  const segs = segmentsOf(path);
  let node: PropertyFileTreeNode = root;
  let acc = '';
  for (const seg of segs) {
    acc = acc ? `${acc}/${seg}` : seg;
    const next = node.folders.find((f) => f.path === acc);
    if (!next) return null;
    node = next;
  }
  return node;
}

export interface Crumb {
  name: string;
  path: string;
}

/** Breadcrumb-Segmente für einen Ordner-Pfad (ohne den „Akte"-Root — den setzt die UI davor). */
export function breadcrumbSegments(path: string): Crumb[] {
  const segs = segmentsOf(path);
  const crumbs: Crumb[] = [];
  let acc = '';
  for (const seg of segs) {
    acc = acc ? `${acc}/${seg}` : seg;
    crumbs.push({ name: seg, path: acc });
  }
  return crumbs;
}

/** Alle Ordner-Knoten unter einem Node flach sammeln (self exklusive), tief-zuerst stabil. */
export function collectFolderNodes(node: PropertyFileTreeNode): PropertyFileTreeNode[] {
  const out: PropertyFileTreeNode[] = [];
  for (const sub of node.folders) {
    out.push(sub);
    out.push(...collectFolderNodes(sub));
  }
  return out;
}

/**
 * Datei-Suche im aktuellen Ordner-Teilbaum: filtert nach Dateiname und Gegenpartei
 * (case-insensitive). Leerer Query → alle Dateien unverändert.
 */
export function matchesQuery(file: PropertyFileResponse, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    file.fileName.toLowerCase().includes(q) ||
    (file.counterparty?.toLowerCase().includes(q) ?? false)
  );
}

/** Alle Dateien eines Node rekursiv (für die Suche über den Teilbaum). */
export function collectFilesDeep(node: PropertyFileTreeNode): PropertyFileResponse[] {
  const out: PropertyFileResponse[] = [...node.files];
  for (const sub of node.folders) out.push(...collectFilesDeep(sub));
  return out;
}

/** Eine Datei per Id im gesamten Baum suchen (für die Detail-Pane nach Tree-Refetch). */
export function findFileById(
  root: PropertyFileTreeNode,
  fileId: string,
): PropertyFileResponse | null {
  return collectFilesDeep(root).find((f) => f.id === fileId) ?? null;
}
